// src/pages/LeadStatusList.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus, FaHashtag, FaPalette, FaInbox } from 'react-icons/fa';
import { getLeadStatuses, deleteLeadStatus } from './leadStatusService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import Pagination from '../../components/shared/Pagination';
import SearchInput from '../../components/shared/SearchInput';
import { useNavigate } from 'react-router-dom';
import {
  GridContainer,
  Card,
  CardHeader,
  LeadName,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  LoadingCard
} from '../../components/styled/GridComponents';
import { ResponsiveHeader } from '../../components/styled/CommonStyles';

const ColorPreview = styled.div`
  width: 100%;
  height: 4px;
  background: ${props => props.color};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  border-top-left-radius: ${props => props.theme.borderRadius};
  border-top-right-radius: ${props => props.theme.borderRadius};
`;

const ColorSwatch = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => props.color};
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  margin-right: ${props => props.theme.spacing(2)};
`;

const StatusCard = styled(Card)`
  padding-top: ${props => props.theme.spacing(5)};
  
  &:hover ${ColorPreview} {
    height: 6px;
  }
`;

// Styled Components for Table
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${(props) => props.theme.spacing(4)};
  th, td {
    padding: ${(props) => props.theme.spacing(2)};
    border-bottom: 1px solid ${(props) => props.theme.colors.inputBorder};
  }
  th {
    background-color: ${(props) => props.theme.colors.primary};
    color: white;
  }
  td {
    text-align: left;
  }
`;

const ColorBox = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${(props) => props.color};
  border-radius: 5px;
  float: left;
  margin-right: 10px;
`;

const ActionIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${(props) => props.theme.spacing(2)};
  cursor: pointer;
  svg {
    &:hover {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing(4)};
`;

const LeadStatusList = () => {
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  

  useEffect(() => {
    const fetchLeadStatuses = async () => {
      setLoading(true);
      try{

        const response = await getLeadStatuses();
        setLeadStatuses(response.data);
        setFilteredStatuses(response.data);
      }catch(err){
        setError('Failed to load lead statuses');
        console.error('Error fetching lead statuses:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeadStatuses();
  }, []);

  // Handle search query
  useEffect(() => {
    const filtered = leadStatuses.filter((status) =>
      status.status_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredStatuses(filtered);
    setCurrentPage(1); // Reset pagination on search
  }, [searchQuery, leadStatuses]);

  // Delete lead status
  const handleDelete = async () => {
    await deleteLeadStatus(statusToDelete);
    setDeleteModalOpen(false);
    setStatusToDelete(null);
    const updatedStatuses = leadStatuses.filter(status => status.id !== statusToDelete);
    setLeadStatuses(updatedStatuses);
    setFilteredStatuses(updatedStatuses);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStatuses = filteredStatuses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Lead Statuses"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/leads/status/new')}
      />

      <GridContainer>
        {loading ? (
          <>
            {[...Array(6)].map((_, index) => (
              <LoadingCard key={index}>
                <CardHeader>
                  <div style={{ width: '60%', height: '24px' }} />
                </CardHeader>
                <CardContent>
                  <div style={{ width: '40%', height: '18px' }} />
                </CardContent>
              </LoadingCard>
            ))}
          </>
        ) : currentStatuses.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No status found. Try adjusting your search or add a new status.</p>
          </EmptyState>
        ) : (
          currentStatuses.map((status) => (
            <StatusCard key={status.id}>
              <ColorPreview color={status.color} />
              <CardHeader>
                <LeadName>
                  {status.status_name}
                </LeadName>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <FaPalette />
                  <span>Color: {status.color}</span>
                </InfoRow>

                <InfoRow>
                  <FaHashtag />
                  <span>ID: {status.id}</span>
                </InfoRow>
              </CardContent>

              <ActionIconsContainer>
                <ActionButton
                  onClick={() => navigate(`/leads/status/edit/${status.id}`)}
                  title="Edit Status"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setStatusToDelete(status.id);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete Status"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </StatusCard>
          ))
        )}
      </GridContainer>

      {!loading && filteredStatuses.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredStatuses.length}
          paginate={(pageNumber) => setCurrentPage(pageNumber)}
          currentPage={currentPage}
        />
      )}

      {deleteModalOpen && (
        <Modal
          title="Confirm Delete"
          message="Are you sure you want to delete this lead status?"
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setStatusToDelete(null);
          }}
          isOpen={deleteModalOpen}
        />
      )}
    </DashboardLayout>
  );
};

export default LeadStatusList;
